import React, { useState, useEffect, useCallback } from 'react';
import { Search, Shield, ArrowLeft, Check, Plus, ShoppingCart, Home, Phone, MessageCircle, ChevronRight, FlaskRound, X, Calendar, Clock, TestTube, Droplets, Timer, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ICONS = {
  'Preventive Health Checkup': '🛡',
  'General Wellness': '🌿',
  'Diabetes': '🩸',
  'Cardiac Risk': '❤',
  "Women's Health": '♀',
  "Men's Health": '♂',
  'Fertility': '👶',
  'Senior Care': '🏥',
  'Kidney': '🫘',
  'Liver': '🫁',
  'Thyroid': '🦋',
  'Anaemia / Nutrition': '💊',
  'Vitamin / Mineral': '🧬',
  'Bone Health': '🦴',
  'Rheumatology': '🦿',
  'Metabolic': '⚡',
  'Allergy': '🤧',
  'Lifestyle Impact': '🏃',
};

const HealthPackages = () => {
  const { cartItems, addToCart, removeFromCart, getTotal, clearCart } = useCart();
  const [profiles, setProfiles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [compareData, setCompareData] = useState([]);
  const [showDetailCheckout, setShowDetailCheckout] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '', phone: '', email: '', address: '',
    home_collection: false, collection_date: '', collection_time: '',
  });

  useEffect(() => {
    axios.get(`${API}/health-profiles/categories`).then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    try {
      const params = selectedCategory ? `?category=${encodeURIComponent(selectedCategory)}` : '';
      const { data } = await axios.get(`${API}/health-profiles${params}`);
      setProfiles(data);
    } catch { toast.error('Failed to load packages'); }
    setLoading(false);
  }, [selectedCategory]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);

  const openDetail = async (profileId) => {
    setDetailLoading(true);
    setShowDetailCheckout(false);
    try {
      const { data } = await axios.get(`${API}/health-profiles/${profileId}`);
      setSelectedProfile(data);
    } catch { toast.error('Failed to load package details'); }
    setDetailLoading(false);
  };

  const isProfileInCart = (name) => cartItems.some(item => (item.test_name || item.name) === name);

  const handleAddPackage = (profile) => {
    addToCart({ ...profile, test_name: profile.name, name: profile.name, price: profile.selling_price, category: profile.category });
    toast.success(`${profile.name} added to cart`);
  };

  const sendWhatsAppNotification = (details) => {
    const phone = '917010200869';
    const testList = details.tests.map(t => `${t.test_name || t.name} (Rs.${t.price})`).join(', ');
    const message = `NEW BOOKING - Aaric Health Packages\nName: ${details.customer_name}\nPhone: ${details.phone}\nEmail: ${details.email}\nPackages/Tests: ${testList}\nTotal: Rs.${details.total_amount}\nHome Collection: ${details.home_collection ? 'Yes' : 'No'}${details.home_collection ? `\nDate: ${details.collection_date} Time: ${details.collection_time}` : ''}\nAddress: ${details.address}`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    if (cartItems.length === 0) { toast.error('Cart is empty'); return; }
    if (!formData.customer_name || !formData.phone) { toast.error('Name and phone required'); return; }
    try {
      const orderData = { ...formData, tests: cartItems, total_amount: getTotal() };
      await axios.post(`${API}/lab-orders`, orderData);
      toast.success('Booking confirmed!');
      sendWhatsAppNotification(orderData);
      clearCart();
      setFormData({ customer_name: '', phone: '', email: '', address: '', home_collection: false, collection_date: '', collection_time: '' });
      setShowCheckout(false);
    } catch { toast.error('Failed to place order'); }
  };

  const toggleCompare = (profileId) => {
    setCompareList(prev => {
      if (prev.includes(profileId)) return prev.filter(id => id !== profileId);
      if (prev.length >= 2) { toast.error('You can compare up to 2 packages'); return prev; }
      return [...prev, profileId];
    });
  };

  const startCompare = async () => {
    if (compareList.length !== 2) { toast.error('Select exactly 2 packages to compare'); return; }
    try {
      const [a, b] = await Promise.all(compareList.map(id => axios.get(`${API}/health-profiles/${id}`)));
      setCompareData([a.data, b.data]);
      setShowCompare(true);
    } catch { toast.error('Failed to load comparison'); }
  };

  const filtered = profiles.filter(p =>
    !searchQuery || p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.primary_use?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const grouped = {};
  filtered.forEach(p => { if (!grouped[p.category]) grouped[p.category] = []; grouped[p.category].push(p); });

  // Compare View
  if (showCompare && compareData.length === 2) {
    const [a, b] = compareData;
    const allTestNames = new Set();
    (a.included_tests || []).forEach(t => allTestNames.add(t.name));
    (b.included_tests || []).forEach(t => allTestNames.add(t.name));
    const aTestNames = new Set((a.included_tests || []).map(t => t.name));
    const bTestNames = new Set((b.included_tests || []).map(t => t.name));
    const sortedTests = [...allTestNames].sort();

    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-10 px-4">
          <div className="max-w-5xl mx-auto">
            <button onClick={() => { setShowCompare(false); setCompareData([]); setCompareList([]); }} className="flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4" data-testid="back-from-compare">
              <ArrowLeft className="h-4 w-4" /> Back to Packages
            </button>
            <h1 className="text-2xl sm:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Scale className="h-7 w-7 inline mr-2" /> Package Comparison
            </h1>
          </div>
        </section>

        <div className="max-w-5xl mx-auto px-4 py-6">
          {/* Header comparison */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="font-bold text-[#0A2540] text-sm self-end pb-2">Feature</div>
            {[a, b].map(p => (
              <div key={p.profile_id} className="bg-white rounded-2xl p-5 shadow-sm text-center" data-testid={`compare-header-${p.profile_id}`}>
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">{p.category}</p>
                <h3 className="font-bold text-[#0A2540] text-base mb-2">{p.name}</h3>
                <p className="text-2xl font-bold text-[#0EA5E9]">Rs.{p.selling_price}</p>
                {p.savings > 0 && <p className="text-xs text-green-600 font-bold">Save Rs.{p.savings}</p>}
                <Button onClick={() => { const inCart = isProfileInCart(p.name); inCart ? removeFromCart(p.name) : handleAddPackage(p); }}
                  className={`mt-3 w-full rounded-xl text-sm h-9 ${isProfileInCart(p.name) ? 'bg-green-600' : 'bg-[#0A2540]'}`}
                  data-testid={`compare-book-${p.profile_id}`}>
                  {isProfileInCart(p.name) ? <><Check className="h-3 w-3 mr-1" /> Added</> : 'Book'}
                </Button>
              </div>
            ))}
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-sm font-medium text-[#475569]">Tests Included</div>
            <div className="text-center font-bold text-[#0A2540]">{a.tests_count}</div>
            <div className="text-center font-bold text-[#0A2540]">{b.tests_count}</div>
          </div>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-sm font-medium text-[#475569]">Reference MRP</div>
            <div className="text-center text-[#94A3B8]">Rs.{a.reference_mrp}</div>
            <div className="text-center text-[#94A3B8]">Rs.{b.reference_mrp}</div>
          </div>
          {(a.primary_use || b.primary_use) && (
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-sm font-medium text-[#475569]">Primary Use</div>
              <div className="text-center text-xs text-[#475569]">{a.primary_use || '-'}</div>
              <div className="text-center text-xs text-[#475569]">{b.primary_use || '-'}</div>
            </div>
          )}

          {/* Test-by-test comparison */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mt-6">
            <div className="grid grid-cols-3 gap-4 p-4 bg-[#F8FAFC] border-b font-bold text-xs text-[#0A2540] uppercase tracking-wider">
              <div>Test Name</div>
              <div className="text-center">{a.name}</div>
              <div className="text-center">{b.name}</div>
            </div>
            {sortedTests.map(testName => (
              <div key={testName} className="grid grid-cols-3 gap-4 p-3 border-b border-[#F1F5F9] text-sm">
                <div className="text-[#475569] truncate">{testName}</div>
                <div className="text-center">
                  {aTestNames.has(testName) ? <Check className="h-4 w-4 text-green-600 mx-auto" /> : <X className="h-4 w-4 text-[#E2E8F0] mx-auto" />}
                </div>
                <div className="text-center">
                  {bTestNames.has(testName) ? <Check className="h-4 w-4 text-green-600 mx-auto" /> : <X className="h-4 w-4 text-[#E2E8F0] mx-auto" />}
                </div>
              </div>
            ))}
          </div>

          {/* Common vs unique */}
          <div className="grid grid-cols-2 gap-4 mt-6">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="font-bold text-green-800 text-xs mb-2">Common Tests ({[...aTestNames].filter(n => bTestNames.has(n)).length})</p>
              <div className="flex flex-wrap gap-1">
                {[...aTestNames].filter(n => bTestNames.has(n)).map(n => <span key={n} className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded">{n}</span>)}
              </div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="font-bold text-blue-800 text-xs mb-2">Unique to each</p>
              <p className="text-[10px] text-blue-600 font-medium mb-1">{a.name}:</p>
              <div className="flex flex-wrap gap-1 mb-2">
                {[...aTestNames].filter(n => !bTestNames.has(n)).map(n => <span key={n} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{n}</span>)}
                {[...aTestNames].filter(n => !bTestNames.has(n)).length === 0 && <span className="text-[10px] text-blue-400">None</span>}
              </div>
              <p className="text-[10px] text-blue-600 font-medium mb-1">{b.name}:</p>
              <div className="flex flex-wrap gap-1">
                {[...bTestNames].filter(n => !aTestNames.has(n)).map(n => <span key={n} className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded">{n}</span>)}
                {[...bTestNames].filter(n => !aTestNames.has(n)).length === 0 && <span className="text-[10px] text-blue-400">None</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Profile Detail View
  if (selectedProfile) {
    const p = selectedProfile;
    const inCart = isProfileInCart(p.name);
    return (
      <div className="min-h-screen bg-[#F8FAFC]">
        <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-10 px-4">
          <div className="max-w-4xl mx-auto">
            <button onClick={() => setSelectedProfile(null)} className="flex items-center gap-1 text-white/70 hover:text-white text-sm mb-4" data-testid="back-to-packages">
              <ArrowLeft className="h-4 w-4" /> Back to Packages
            </button>
            <p className="text-white/60 text-xs uppercase tracking-wider mb-1">{p.category}</p>
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="profile-name">{p.name}</h1>
            <div className="flex flex-wrap gap-4 items-baseline">
              <span className="text-3xl font-bold text-white">Rs.{p.selling_price}</span>
              {p.savings > 0 && (
                <>
                  <span className="text-lg text-white/50 line-through">Rs.{p.reference_mrp}</span>
                  <span className="bg-green-400/20 text-green-300 px-3 py-1 rounded-full text-sm font-bold">Save Rs.{p.savings}</span>
                </>
              )}
            </div>
          </div>
        </section>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
          {/* Info Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {p.primary_use && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">Primary Use</p>
                <p className="text-sm font-medium text-[#0A2540]">{p.primary_use}</p>
              </div>
            )}
            {p.ideal_for && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">Ideal For</p>
                <p className="text-sm font-medium text-[#0A2540]">{p.ideal_for}</p>
              </div>
            )}
            {p.who_should_take && (
              <div className="bg-white rounded-xl p-4 shadow-sm">
                <p className="text-xs text-[#94A3B8] uppercase tracking-wider mb-1">Who Should Take</p>
                <p className="text-sm font-medium text-[#0A2540]">{p.who_should_take}</p>
              </div>
            )}
          </div>

          {/* Included Tests */}
          <div className="bg-white rounded-2xl p-5 shadow-sm">
            <h2 className="font-bold text-[#0A2540] text-lg mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Tests Included ({p.tests_count})
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(p.included_tests || []).map(t => (
                <div key={t.test_code} className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-xl" data-testid={`included-test-${t.test_code}`}>
                  <div className="p-1.5 bg-sky-50 rounded-lg"><TestTube className="h-4 w-4 text-sky-600" /></div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-[#0A2540] truncate">{t.name}</p>
                    <div className="flex gap-2 text-[10px] text-[#94A3B8]">
                      {t.sample_requirement && <span>{t.sample_requirement}</span>}
                      {t.tat && <span>{t.tat}</span>}
                    </div>
                  </div>
                  <span className="text-xs text-[#94A3B8] whitespace-nowrap">Rs.{t.mrp}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Home Collection */}
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center gap-4">
            <Home className="h-8 w-8 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold text-green-800 text-sm">Home Sample Collection Available</p>
              <p className="text-xs text-green-700">Our phlebotomist will visit your doorstep</p>
            </div>
          </div>

          {/* Booking Section */}
          {!showDetailCheckout ? (
            <Button onClick={() => setShowDetailCheckout(true)}
              className="w-full h-14 rounded-xl text-lg font-bold bg-[#0A2540]"
              data-testid="book-package-btn">
              Book Now - Rs.{p.selling_price}
            </Button>
          ) : (
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-[#E2E8F0]" data-testid="detail-checkout-form">
              <h2 className="text-xl font-bold text-[#0A2540] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Book {p.name}</h2>
              <p className="text-sm text-[#94A3B8] mb-4">Package Price: <span className="font-bold text-[#0EA5E9]">Rs.{p.selling_price}</span></p>
              <form onSubmit={(e) => {
                e.preventDefault();
                if (!formData.customer_name || !formData.phone) { toast.error('Name and phone required'); return; }
                const orderData = {
                  ...formData, tests: [{ test_name: p.name, name: p.name, price: p.selling_price, category: p.category, profile_id: p.profile_id }],
                  total_amount: p.selling_price,
                };
                axios.post(`${API}/lab-orders`, orderData).then(() => {
                  toast.success('Booking confirmed! We will contact you shortly.');
                  sendWhatsAppNotification(orderData);
                  setFormData({ customer_name: '', phone: '', email: '', address: '', home_collection: false, collection_date: '', collection_time: '' });
                  setShowDetailCheckout(false);
                }).catch(() => toast.error('Failed to place order'));
              }} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Full Name *</Label><Input value={formData.customer_name} onChange={e => setFormData(pr => ({ ...pr, customer_name: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="detail-checkout-name" /></div>
                  <div><Label>Phone *</Label><Input value={formData.phone} onChange={e => setFormData(pr => ({ ...pr, phone: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="detail-checkout-phone" /></div>
                </div>
                <div><Label>Email</Label><Input value={formData.email} onChange={e => setFormData(pr => ({ ...pr, email: e.target.value }))} className="h-11 rounded-xl mt-1" /></div>
                <div><Label>Address</Label><Input value={formData.address} onChange={e => setFormData(pr => ({ ...pr, address: e.target.value }))} className="h-11 rounded-xl mt-1" /></div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="hc_detail" checked={formData.home_collection} onCheckedChange={v => setFormData(pr => ({ ...pr, home_collection: v }))} data-testid="detail-home-collection" />
                  <label htmlFor="hc_detail" className="text-sm font-medium flex items-center"><Home className="h-4 w-4 mr-2 text-[#0EA5E9]" /> Home Sample Collection</label>
                </div>
                {formData.home_collection && (
                  <div className="grid grid-cols-2 gap-3 bg-[#0EA5E9]/5 p-4 rounded-xl">
                    <div><Label><Calendar className="h-3 w-3 inline mr-1" />Date *</Label><Input type="date" value={formData.collection_date} onChange={e => setFormData(pr => ({ ...pr, collection_date: e.target.value }))} required={formData.home_collection} className="h-11 rounded-xl mt-1" data-testid="detail-collection-date" /></div>
                    <div><Label><Clock className="h-3 w-3 inline mr-1" />Time *</Label><Input type="time" value={formData.collection_time} onChange={e => setFormData(pr => ({ ...pr, collection_time: e.target.value }))} required={formData.home_collection} className="h-11 rounded-xl mt-1" data-testid="detail-collection-time" /></div>
                  </div>
                )}
                <div className="flex gap-2">
                  <Button type="submit" className="flex-1 bg-[#0A2540] text-white rounded-xl py-3 text-lg font-bold" data-testid="detail-submit-booking">
                    <MessageCircle className="h-5 w-5 mr-2" /> Confirm Booking - Rs.{p.selling_price}
                  </Button>
                  <Button type="button" onClick={() => setShowDetailCheckout(false)} variant="outline" className="rounded-xl py-3 px-4">Cancel</Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#0A2540] to-[#0EA5E9] py-12 sm:py-16 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-5 py-2 mb-4">
            <Shield className="h-4 w-4 text-green-300" />
            <span className="text-white text-sm font-semibold">NABL M(EL)T Accredited</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="health-packages-heading">
            Health Packages & Profiles
          </h1>
          <p className="text-base sm:text-lg text-white/85 mb-4 max-w-2xl mx-auto">
            {profiles.length} curated health packages. Save more with bundled tests.
          </p>
          <a href="/lab-tests" className="inline-flex items-center gap-2 bg-white/15 hover:bg-white/25 text-white rounded-full px-6 py-2.5 text-sm font-semibold border border-white/20 transition-all">
            View Individual Lab Tests
          </a>
        </div>
      </section>

      {/* Search + Cart */}
      <section className="py-3 px-4 bg-white border-b sticky top-0 z-40 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94A3B8]" />
            <Input placeholder="Search packages..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-10 h-10 rounded-xl" data-testid="search-packages-input" />
          </div>
          {cartItems.length > 0 && (
            <Button onClick={() => setShowCheckout(!showCheckout)} className="bg-[#0EA5E9] text-white rounded-xl h-10 px-4" data-testid="packages-cart-btn">
              <ShoppingCart className="h-4 w-4 mr-1" /> {cartItems.length} | Rs.{getTotal()}
            </Button>
          )}
        </div>
      </section>

      {/* Category Tabs */}
      <section className="px-4 py-3 bg-white border-b">
        <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto pb-1">
          <button onClick={() => setSelectedCategory('')}
            className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap ${!selectedCategory ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}
            data-testid="pkg-category-all">All</button>
          {categories.map(cat => (
            <button key={cat} onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-bold whitespace-nowrap ${selectedCategory === cat ? 'bg-[#0A2540] text-white' : 'bg-[#F1F5F9] text-[#475569]'}`}
              data-testid={`pkg-category-${cat.toLowerCase().replace(/[\s/]+/g, '-')}`}>{cat}</button>
          ))}
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {showCheckout ? (
          <div className="bg-white rounded-2xl p-6 shadow-lg max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold text-[#0A2540] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Book Packages</h2>
            <div className="mb-6 space-y-2">
              {cartItems.map(item => (
                <div key={item.test_name || item.name} className="flex items-center justify-between p-3 bg-[#F8FAFC] rounded-xl">
                  <div><p className="font-medium text-sm text-[#0A2540]">{item.test_name || item.name}</p><p className="text-xs text-[#475569]">{item.category}</p></div>
                  <div className="flex items-center gap-3">
                    <span className="font-bold text-[#0A2540]">Rs.{item.price}</span>
                    <button onClick={() => removeFromCart(item.test_name || item.name)} className="text-red-400 hover:text-red-600"><X className="h-4 w-4" /></button>
                  </div>
                </div>
              ))}
              <div className="pt-3 border-t flex justify-between"><span className="font-bold text-lg">Total</span><span className="font-bold text-lg text-[#0EA5E9]">Rs.{getTotal()}</span></div>
            </div>
            <form onSubmit={handleSubmitOrder} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Full Name *</Label><Input value={formData.customer_name} onChange={e => setFormData(p => ({ ...p, customer_name: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="pkg-checkout-name" /></div>
                <div><Label>Phone *</Label><Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} required className="h-11 rounded-xl mt-1" data-testid="pkg-checkout-phone" /></div>
              </div>
              <div><Label>Email</Label><Input value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} className="h-11 rounded-xl mt-1" /></div>
              <div><Label>Address</Label><Input value={formData.address} onChange={e => setFormData(p => ({ ...p, address: e.target.value }))} className="h-11 rounded-xl mt-1" /></div>
              <div className="flex items-center space-x-2">
                <Checkbox id="hc_pkg" checked={formData.home_collection} onCheckedChange={v => setFormData(p => ({ ...p, home_collection: v }))} />
                <label htmlFor="hc_pkg" className="text-sm font-medium flex items-center"><Home className="h-4 w-4 mr-2 text-[#0EA5E9]" /> Home Sample Collection</label>
              </div>
              {formData.home_collection && (
                <div className="grid grid-cols-2 gap-3 bg-[#0EA5E9]/5 p-4 rounded-xl">
                  <div><Label>Date *</Label><Input type="date" value={formData.collection_date} onChange={e => setFormData(p => ({ ...p, collection_date: e.target.value }))} required className="h-11 rounded-xl mt-1" /></div>
                  <div><Label>Time *</Label><Input type="time" value={formData.collection_time} onChange={e => setFormData(p => ({ ...p, collection_time: e.target.value }))} required className="h-11 rounded-xl mt-1" /></div>
                </div>
              )}
              <Button type="submit" className="w-full bg-[#0A2540] text-white rounded-xl py-3 text-lg font-bold" data-testid="pkg-submit-order">
                <MessageCircle className="h-5 w-5 mr-2" /> Confirm Booking - Rs.{getTotal()}
              </Button>
            </form>
          </div>
        ) : (
          <>
            {loading ? <p className="text-center text-[#94A3B8] py-8">Loading packages...</p> : Object.keys(grouped).length === 0 ? <p className="text-center text-[#94A3B8] py-8">No packages found</p> : (
              <div className="space-y-8">
                {Object.entries(grouped).map(([cat, items]) => (
                  <div key={cat}>
                    <h2 className="text-lg font-bold text-[#0A2540] mb-3 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <span>{CATEGORY_ICONS[cat] || '📋'}</span> {cat}
                      <span className="text-sm font-normal text-[#94A3B8]">({items.length})</span>
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {items.map(p => {
                        const inCart = isProfileInCart(p.name);
                        return (
                          <div key={p.profile_id} className={`bg-white border rounded-2xl p-5 hover:shadow-lg transition-all flex flex-col ${compareList.includes(p.profile_id) ? 'border-violet-400 ring-2 ring-violet-100' : 'border-[#E2E8F0]'}`} data-testid={`profile-card-${p.profile_id}`}>
                            <div className="flex items-start justify-between mb-1">
                              <p className="text-xs text-[#94A3B8] uppercase tracking-wider">{p.category}</p>
                              <button onClick={(e) => { e.stopPropagation(); toggleCompare(p.profile_id); }}
                                className={`p-1 rounded-md text-xs ${compareList.includes(p.profile_id) ? 'bg-violet-100 text-violet-700' : 'bg-[#F1F5F9] text-[#94A3B8] hover:bg-violet-50 hover:text-violet-500'}`}
                                data-testid={`compare-toggle-${p.profile_id}`} title="Compare">
                                <Scale className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            <h3 className="font-bold text-[#0A2540] text-base mb-2 leading-tight flex-1">{p.name}</h3>
                            {p.primary_use && <p className="text-xs text-[#475569] mb-3">{p.primary_use}</p>}
                            <div className="flex items-center gap-2 text-xs text-[#64748B] mb-3">
                              <FlaskRound className="h-3.5 w-3.5" />
                              <span>{p.tests_count} tests included</span>
                            </div>
                            <div className="flex items-baseline gap-2 mb-1">
                              <span className="text-2xl font-bold text-[#0EA5E9]">Rs.{p.selling_price}</span>
                              {p.savings > 0 && <span className="text-sm text-[#94A3B8] line-through">Rs.{p.reference_mrp}</span>}
                            </div>
                            {p.savings > 0 && <p className="text-xs text-green-600 font-bold mb-3">Save Rs.{p.savings}</p>}
                            <div className="flex gap-2 mt-auto">
                              <Button onClick={() => openDetail(p.profile_id)} variant="outline" className="flex-1 rounded-xl text-sm h-9 border-[#0A2540] text-[#0A2540]" data-testid={`view-tests-${p.profile_id}`}>
                                View Tests <ChevronRight className="h-3 w-3 ml-1" />
                              </Button>
                              <Button onClick={() => inCart ? removeFromCart(p.name) : handleAddPackage(p)}
                                className={`flex-1 rounded-xl text-sm h-9 ${inCart ? 'bg-green-600' : 'bg-[#0A2540]'}`}
                                data-testid={`book-profile-${p.profile_id}`}>
                                {inCart ? <><Check className="h-3 w-3 mr-1" /> Added</> : 'Book'}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Floating Compare Bar */}
      {compareList.length > 0 && !showCheckout && (
        <div className="fixed bottom-0 inset-x-0 bg-white border-t-2 border-violet-400 shadow-2xl p-4 z-50" data-testid="compare-bar">
          <div className="max-w-6xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Scale className="h-5 w-5 text-violet-600" />
              <span className="text-sm font-medium text-[#0A2540]">
                {compareList.length}/2 packages selected
                {compareList.length === 1 && ' — select 1 more to compare'}
              </span>
              <div className="flex gap-1">
                {compareList.map(id => {
                  const p = profiles.find(x => x.profile_id === id);
                  return (
                    <span key={id} className="bg-violet-100 text-violet-700 text-xs px-2 py-1 rounded-lg flex items-center gap-1">
                      {p?.name || id}
                      <button onClick={() => toggleCompare(id)}><X className="h-3 w-3" /></button>
                    </span>
                  );
                })}
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setCompareList([])} variant="outline" className="rounded-xl h-9 px-3 text-sm">Clear</Button>
              <Button onClick={startCompare} disabled={compareList.length !== 2} className="bg-violet-600 hover:bg-violet-700 rounded-xl h-9 px-5 text-sm" data-testid="compare-btn">
                Compare Now
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HealthPackages;
